import { type Ref, ref } from 'vue'

import { createPerTickGuard } from '@/util/perTickGuard'

interface PopoverPlaybackOptions<K> {
  equals?: (a: K, b: K) => boolean
  paused: Ref<boolean>
  playFromStart: (key: K) => void
  resume: () => void
}

/**
 * Decides a segment popover's play / pause / resume / restart action.
 * `start` plays a key from the beginning and is per-tick-guarded so a bubbled
 * segment-click + word-click in one tick fires once. `playOrResume` resumes when
 * paused on the same key, otherwise starts fresh.
 */
export function usePopoverPlayback<K>({ equals, paused, playFromStart, resume }: PopoverPlaybackOptions<K>) {
  // ref<K | null> widens to Ref<UnwrapRef<K> | null> for abstract K; cast keeps the K type.
  const lastPlayedKey = ref<K | null>(null) as Ref<K | null>
  const sameKey = equals ?? ((a: K, b: K): boolean => a === b)

  const start = createPerTickGuard((key: K) => {
    lastPlayedKey.value = key
    playFromStart(key)
  })

  const playOrResume = (key: K): void => {
    const last = lastPlayedKey.value
    if (paused.value && last !== null && sameKey(key, last)) {
      resume()
    } else {
      start(key)
    }
  }

  const reset = (): void => {
    lastPlayedKey.value = null
  }

  return { lastPlayedKey, playOrResume, reset, start }
}
